import { node } from "~/util/dom";
import ReportFormField from "./index";
import { TEXTAREA_MIN_HEIGHT } from "~/report/const";
import { ICommonOpts } from "~/report/interfaces";

interface IOpts extends ICommonOpts {
	minHeight?: number;
	insertHTML?: boolean;
	container: HTMLElement;
	name: string;
	label?: string;
	sublabel?: string;
	subsublabel?: Array<string>;
	placeholder?: string;
	value?: string;
	maxlength?: number;
	getMessage?: (value: string) => string;
	typeSign?: HTMLElement|SVGElement;
	changeSign?: HTMLElement|SVGElement;
	onChange: (value: string, data: object) => void;
}

export default class ReportFormFieldTextarea extends ReportFormField {
	#opts: IOpts;

	#originalValue = "";

	#textareaNode: HTMLTextAreaElement;

	#messageNode: HTMLElement;

	#getMessage: (value: string) => string;

	constructor(opts: IOpts) {
		super(opts);
		this.#opts = {
			parent: null,
			minHeight: TEXTAREA_MIN_HEIGHT,
			insertHTML: false,
			container: null,
			parentName: "",
			name: "",
			label: "",
			sublabel: "",
			subsublabel: [],
			placeholder: "",
			value: "",
			maxlength: 0,
			getMessage: () => ``,
			typeSign: null,
			changeSign: null,
			onChange: () => { /**/ },
			...opts,
		};
		this.#build();
	}

	get inputNode() {
		return this.#textareaNode;
	}

	handleEvent(event: Event) {
		if (event.type === "input") {
			const { value } = this.#textareaNode;

			this.#updateValue(value);
		}

		if (super.container.classList.contains("simple")) {
			if (event.type === "focus") {
				super.container.classList.add("focused");
			} else if (event.type === "blur") {
				super.container.classList.remove("focused");
			}
		}
	}

	setHeight(isForced: boolean) {
		const { minHeight } = this.#opts;

		this.#textareaNode.style.height = `${minHeight}px`;
		const { scrollHeight, clientHeight } = this.#textareaNode;

		if (scrollHeight !== clientHeight && scrollHeight > minHeight) {
			this.#textareaNode.style.height = `${scrollHeight}px`;
			this.#opts.container.dispatchEvent(new Event("resize-content"));
		} else if (isForced) {
			this.#opts.container.dispatchEvent(new Event("resize-content"));
		}
	}

	#build() {
		const { parentName, name, label, sublabel, subsublabel, placeholder, value, maxlength, getMessage, typeSign, changeSign, insertHTML } = this.#opts;
		let labelId = null;

		this.#textareaNode = node("textarea");
		this.#textareaNode.id = `${parentName}-${name}-textarea`;

		this.#textareaNode.setAttribute("name", name);
		this.#textareaNode.addEventListener("input", this);

		if (label) {
			const labelNode = node("label", { className: "label" });

			labelNode.id = `${parentName}-${name}-label`;
			labelNode.innerHTML = label.concat(sublabel ? ` <strong>${sublabel}</strong>` : "").concat(subsublabel ? `<br>${subsublabel.join("<br/>")}` : "");
			labelNode.setAttribute("for", this.#textareaNode.id);
			labelId = labelNode.id;
			super.container.append(labelNode);
		} else {
			super.container.classList.add("simple", "top");
			if (typeSign) {
				typeSign.classList.add("type-sign", "no-shrink");
				super.container.append(typeSign);
			}
			this.#textareaNode.addEventListener("focus", this);
			this.#textareaNode.addEventListener("blur", this);
		}
		if (placeholder) {
			this.#textareaNode.setAttribute("placeholder", placeholder);
		}
		if (value) {
			this.#originalValue = value;

			if (insertHTML) {
				this.#textareaNode.innerHTML = value;
			} else {
				this.#textareaNode.innerText = value;
			}

			setTimeout(() => {
				this.#updateValue(value);
				this.#textareaNode.setSelectionRange(value.length, value.length);
			}, 5);
		}
		if (maxlength) {
			this.#textareaNode.setAttribute("maxlength", maxlength.toString());
		}
		super.container.append(this.#textareaNode);
		if (changeSign) {
			changeSign.classList.add("change-sign", "no-shrink");
			super.container.append(changeSign);
		}
		if (typeof getMessage === "function") {
			super.container.classList.add("with-footnote");
			this.#messageNode = node("label", { className: "message" });
			this.#messageNode.id = `${parentName}-${name}-message`;
			this.#messageNode.setAttribute("for", this.#textareaNode.id);
			if (labelId) {
				this.#textareaNode.setAttribute("aria-labeledby", `${labelId} ${this.#messageNode.id}`);
			}
			if (getMessage.length) {
				this.#getMessage = getMessage;
			}
			this.#messageNode.innerHTML = getMessage(value || "");
			super.container.append(this.#messageNode);
		}
		this.setHeight(true);
	}

	#updateValue(value: string) {
		this.setHeight(false);

		if (this.#originalValue !== value) {
			super.container.classList.add("changed");
		} else if (super.container.classList.contains("changed")) {
			super.container.classList.remove("changed");
		}

		if (this.#getMessage) {
			this.#messageNode.innerHTML = this.#getMessage(value);
		}
		this.#opts.onChange(value, null);
	}
}
